import {
    Button,
    Dialog,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import en from '../../../../lang/en-us';
import { SubmitButtonWithLoader } from '../../../../lib/Button';
import { CustomRadio } from '../../../../lib/CustomRadio';
import { TableCellParams, TableWithCollapse } from '../../../../lib/DataTable';
import { useTableWithCollapse } from '../../../../lib/DataTable/customHooks';
import { TableColumn } from '../../../../lib/DataTable/types';
import TextInput from '../../../../lib/TextField';
import { getAmcList } from '../../../../redux-store/actions/nct';
import {
    checkOnlinePayment,
    getOtmData,
    paywithOTM,
    trxnPayOnline,
} from '../../../../redux-store/actions/newPurchase';
import { CLEAR_SCHEME_INFO, OtmTrxnRequestType } from '../../../../redux-store/types/newPurchase';
import { IsAllow } from '../../../../redux-store/types/portfolio';
import { isMobileDevice } from '../../../../utils/constants';
import { formatAsIndianCurrency } from '../../../../utils/currency';
import { maskAccountNo } from '../../../../utils/utilityFunctions';
import { ActionHeader } from '../../makeNct';
import NoFoliosFound from '../../makeNct/NoFoliosFound';
import { CommercialRootPath } from '../routeConfig';
import { displayTransactionType } from '../utils';
import mfInvestment from './mf-investment';
import { NewPurchasePayload, Trxntypes } from './types';
import { appSchemePattern, extractUpiUrl, normalizeUpiUrl } from './upiIntent';
import { otmSipDateConditionNotValid } from './Validations';

const { dbCommercial } = en;

enum PaymentModeType {
    NB = 'NB',
    UPI = 'UPI',
    OTM = 'OTM',
}

interface Props { }

const _renderOtmData = ({ row, setCollapse, collapse }: TableCellParams) => {
    return (
        <>
            <IconButton
                className="update-nominee-collapse-btn"
                aria-label="expand row"
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    setCollapse((open: boolean) => !open);
                }}
            >
                {!collapse ? (
                    <>
                        <span>View</span>
                        <KeyboardArrowDown />
                    </>
                ) : (
                    <>
                        <span>Hide</span>
                        <KeyboardArrowUp />
                    </>
                )}
            </IconButton>
        </>
    );
};

const OtmDataColumns: TableColumn[] = [
    {
        field: 'id',
        headerName: 'UMRN Number',
        fullWidth: true,
        valueGetter: ({ row }) => `${row.omUMRN}`,
        width: 250,
    },
    { field: 'maxAmount', headerName: 'Max Amount', width: 100 },
    { field: 'omExpiryDate', headerName: 'Expiry Date', width: 120 },
    {
        field: 'otm',
        renderCell: _renderOtmData,
        headerName: '',
        width: 100,
    },
];

const showCollapseData = ({ row }: TableCellParams) => {
    const values: { label: string; value: string }[] = [
        {
            label: 'Bank Name',
            value: row.bank?.name,
        },
        {
            label: 'Account No.',
            value: row.bank?.accountNo,
        },
        {
            label: 'Ifsc',
            value: row.bank?.ifsc,
        },
        {
            label: 'OTM Type',
            value: row.otmType,
        },
    ];
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                width: '100%',
                flexWrap: 'wrap',
            }}
        >
            {values.map(({ label, value }, index) => (
                <div
                    key={index}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginRight: 0,
                        width: '32%',
                        padding: '10px',
                    }}
                >
                    <label>{label}</label>
                    <label>{value ? value : 'N/A'}</label>
                </div>
            ))}
        </div>
    );
};

enum LoadingStates {
    OTM_FETCH,
    PAY,
}

const getHeading = (trxnType: string) => {
    switch (trxnType) {
        case Trxntypes.AP_LUMPSUM:
            return dbCommercial.additionalPurchase.heading;
        case Trxntypes.AP_SIP:
        case Trxntypes.FP_SIP:
            return dbCommercial.sip.heading;
        case Trxntypes.FP_LUMPSUM:
            return dbCommercial.ctLinks.newPurchase;
        default:
            return '';
    }
};

const MfPayment: React.FunctionComponent<Props> = (props) => {
    const [paymentMode, setPaymentMode] = useState<PaymentModeType | null>(null);
    const [show, setShow] = useState<boolean>(false);
    const [amcList, setamcList] = useState<Array<{ amc_code: string, amc_name: string, rta_code: 'CAMS' | 'KFIN' }> | null>(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const location =
        useLocation<NewPurchasePayload & { schemeName: string; logoUrl: string; folio: string }>();
    const [data, setData] = useState<any>([]);
    const [loading, setLoading] = useState<LoadingStates | null>(null);
    const { amc, schemeOptions = [] } = location.state || {};
    const {
        startDate: fromDate,
        endDate: toDate,
        trxnType,
        sipType,
        amount,
        folio = '',
    } = schemeOptions[0] || {};
    const params = useParams<{ userTrxnNo: string; reqId: string }>();
    const { userTrxnNo, reqId } = params || {};
    const [error, setError] = useState<any>({});
    const [vpa, setVpa] = useState<string>('');

    const {
        selectedRows,
        setSelectedRows,
        resetTable,
        resetFilters,
        tableColumns,
        tableRows,
        ...filters
    } = useTableWithCollapse<any[]>({
        initialSelectionModal: [],
        rows: data,
        columns: OtmDataColumns,
        loading: loading === LoadingStates.OTM_FETCH,
    });

    useEffect(() => {
        if (!location.state) history.push('/');
        else checkPaymentAvailable();
        async function init() {
            let { body }: any = await dispatch(getAmcList());
            setamcList(body);
        }
        init();
    }, []);

    const disableOTMs = (otm: any, type: Trxntypes) => {
        const isKfinFund = otm.rtaName === 'KFIN';
        const isOTMDisabled = otm.otmType === 'AMCOTM' || otm.otmType === 'AMC OTM';
        if (isKfinFund) {
            return isOTMDisabled;
        } else {
            if (type === Trxntypes.FP_LUMPSUM || type === Trxntypes.FP_SIP) {
                return isOTMDisabled;
            }
            const allowOTMForCAMSAdditionalPurchase = (otm.otmAmc === 'ALL' || otm.otmAmc === amc || otm.folio === folio);
            return (isOTMDisabled && !allowOTMForCAMSAdditionalPurchase);
        }
    };

    useEffect(() => {
        async function fetchOtm() {
            const payload: any = {
                amc: amc as string,
                // fromDate,
                // toDate,
                // trxnType,
                // amount,
                // folio,
            };
            try {
                setLoading(LoadingStates.OTM_FETCH);
                const response = await dispatch(getOtmData(payload));
                if (!!response && typeof response === 'object') {
                    const { body = {} }: any = response;
                    const data = (body.data || []).reduce(
                        (prev: any, ele: any) => {
                            const isDisabled =
                                Number(ele.maxAmount) < Number(amount) ||
                                disableOTMs(ele, schemeOptions[0].trxnType as Trxntypes) ||
                                !ele.omUMRN ||
                                ele.status?.toLowerCase() !== 'active' ||
                                (sipType === 'SIP' &&
                                    otmSipDateConditionNotValid(
                                        ele.omStartDate,
                                        ele.omExpiryDate,
                                        fromDate,
                                        toDate,
                                    ));

                            if (isDisabled) {
                                return {
                                    ...prev,
                                    disabled: [
                                        ...prev.disabled,
                                        { ...ele, isDisabled: true, id: ele.omUMRN },
                                    ],
                                };
                            }
                            return {
                                ...prev,
                                enabled: [...prev.enabled, { ...ele, id: ele.omUMRN }],
                            };
                        },
                        { disabled: [], enabled: [] },
                    );
                    setData([...data.enabled, ...data.disabled]);
                }
            } catch (err: any) {
                alert(err.message);
            } finally {
                setLoading(null);
            }
        }
        if (paymentMode === PaymentModeType.OTM) fetchOtm();
    }, [paymentMode]);

    // UPI Intent Handling for Payment Gateways (CAMSPay, Swiftflow)
    //
    // Opens a payment gateway page in Cordova InAppBrowser and intercepts UPI scheme
    // URLs (upi://, phonepe://, tez://, etc.) to launch native UPI apps.
    //
    // Two gateway patterns exist:
    //
    // 1. CAMSPay: User clicks "Pay with UPI" -> API call -> gateway JS navigates to upi://
    //    - First dispatch: onBeforeLoad/customscheme intercepts the navigation event
    //    - Retry: injected click interceptor flags __upiRetryRequested, poller dispatches
    //
    // 2. Swiftflow: User selects UPI app, clicks "Proceed" -> gateway JS navigates to phonepe://
    //    - All dispatches: injected click interceptor reads URL from DOM, sets __pendingUpiUrl,
    //      poller dispatches. This prevents the WebView from ever seeing the custom scheme URL.
    //
    // Why the click interceptor is needed (Swiftflow):
    //   Android WebView has an undocumented behavior where custom scheme navigations blocked by
    //   shouldOverrideUrlLoading (returning true) freeze the WebView's navigation engine entirely.
    //   After the first blocked navigation, ALL subsequent window.location.href assignments are
    //   silently dropped - including normal HTTP URLs. The click interceptor prevents the gateway's
    //   location.href from executing via stopImmediatePropagation, keeping the WebView healthy.
    //
    // Why onBeforeLoad is still needed (CAMSPay):
    //   CAMSPay's UPI URL is generated server-side and not present in the DOM before navigation.
    //   The click interceptor can't read it. onBeforeLoad catches the navigation event and
    //   dispatches the intent. The WebView freezes after this, but CAMSPay's retry button is
    //   handled by the poller, so the freeze doesn't matter.
    //
    // Communication between injected scripts and host code:
    //   - window.__pendingUpiUrl: set by click interceptor, read by poller (Swiftflow flow)
    //   - window.__upiRetryRequested: set by retry button interceptor, read by poller (CAMSPay flow)
    //   - window.__lastUpiUrl: stores last dispatched URL for retry fallback
    function onMobileRedirect(url: string) {
        let upiDispatched = false;
        let lastUpiUrl: string | null = null;
        const _window: any = window;
        const isAndroid = _window?.device?.platform === 'Android';
        const isIOS = _window?.device?.platform === 'iOS';

        const getUpiUrl = (rawUrl: string) => extractUpiUrl(rawUrl, isIOS);
        const toSystemUrl = (upiUrl: string) => (isIOS ? normalizeUpiUrl(upiUrl) : upiUrl);

        // Launches a UPI app via the OS intent system.
        // The 2s guard prevents duplicate dispatches when multiple event handlers
        // (beforeload, loadstart, customscheme) fire for the same navigation.
        const dispatchUpiIntent = (upiUrl: string, source: string) => {
            if (upiDispatched) {
                console.log(`[UPI:${source}] skipped duplicate dispatch`, upiUrl);
                return;
            }
            upiDispatched = true;
            lastUpiUrl = upiUrl;
            console.log(`[UPI:${source}] opening in system`, toSystemUrl(upiUrl));
            _window.cordova.InAppBrowser.open(toSystemUrl(upiUrl), '_system');
            ref.executeScript({ code: `window.__lastUpiUrl = ${JSON.stringify(upiUrl)};` });
            setTimeout(() => {
                upiDispatched = false;
            }, 2000);
        };

        // Primary interception for CAMSPay: catches UPI URLs before the WebView loads them.
        // Not calling callback() for UPI URLs blocks the navigation but freezes the WebView.
        // This is acceptable because CAMSPay retry is handled by the poller.
        const onBeforeLoad = (params: any, callback: any) => {
            console.log('[UPI:beforeload]', params.url);
            const upiUrl = getUpiUrl(params.url);
            if (upiUrl) {
                dispatchUpiIntent(upiUrl, 'beforeload');
                return;
            }
            if (params.url.includes('payment-status')) {
                ref.close();
            } else {
                callback(params.url);
            }
        };

        // Fallback: catches UPI URLs after navigation starts (fires after onBeforeLoad).
        // Also handles payment-status redirects to close the browser.
        const onLoadStart = (event: any) => {
            console.log('[UPI:loadstart]', event.url);
            const upiUrl = getUpiUrl(event.url);
            if (upiUrl) {
                dispatchUpiIntent(upiUrl, 'loadstart');
                return;
            }
            if (event.url.includes('payment-status')) {
                ref.close();
            }
        };

        // Android fallback: fires when AllowedSchemes in config.xml matches the URL scheme.
        // Prevents WebView from showing ERR_UNKNOWN_URL_SCHEME error page.
        const onCustomScheme = (event: any) => {
            console.log('[UPI:customscheme]', event.url);
            const upiUrl = getUpiUrl(event.url);
            if (upiUrl) {
                dispatchUpiIntent(upiUrl, 'customscheme');
            }
        };

        // Last resort fallback: catches UPI URLs that the WebView failed to load.
        const onLoadError = (event: any) => {
            console.log('[UPI:loaderror]', event.url, event.code, event.message);
            const upiUrl = getUpiUrl(event.url);
            if (upiUrl) {
                dispatchUpiIntent(upiUrl, 'loaderror');
            }
        };

        // Injects a click interceptor into the gateway page after each page load.
        // This script runs inside the InAppBrowser's page context (not our app).
        //
        // For Swiftflow: intercepts "Proceed" button clicks BEFORE the gateway's handler
        // can execute window.location.href, preventing the WebView freeze. Reads the
        // selected UPI app URL from the DOM and sets __pendingUpiUrl for the poller.
        //
        // For CAMSPay: intercepts "Retry Payment" button clicks and sets __upiRetryRequested
        // for the poller to re-dispatch the last UPI URL.
        const onLoadStop = () => {
            ref.executeScript({
                code: `
                    if (!window.__upiPatched) {
                        window.__upiPatched = true;
                        var schemes = new RegExp(${JSON.stringify(appSchemePattern(isIOS))}, 'i');
                        // Diagnostic log lines, drained by the poller into the host console
                        var log = function(msg) { (window.__upiLog = window.__upiLog || []).push(msg); };

                        document.addEventListener('click', function(e) {
                            var anchor = e.target.closest('a[href]');
                            if (anchor && schemes.test(anchor.href)) {
                              e.stopImmediatePropagation();
                              e.preventDefault();
                              log('anchor click: ' + anchor.href);
                              window.__pendingUpiUrl = anchor.href;
                              return;
                            }
                            var btn = e.target.closest('button');
                            if (!btn) return;
                            var text = btn.textContent.trim().toLowerCase();

                            // CAMSPay retry flow
                            if (text.includes('retry')) {
                                window.__upiRetryRequested = true;
                                return;
                            }

                            // Swiftflow flow: read UPI URL from multiple sources
                            // 1. Gateway's own global variable (if exposed)
                            var url = window.selectedIntentUrl;
                            // 2. DOM: the selected UPI app's anchor href
                            if (!url || !schemes.test(url)) {
                                var selected = document.querySelector('.selected a[href], .active a[href], [aria-selected="true"] a[href]');
                                if (selected) url = selected.href;
                            }
                            // 3. Fallback: last dispatched URL
                            if (!url || !schemes.test(url)) {
                                url = window.__lastUpiUrl;
                            }
                            var intercepted = !!(url && schemes.test(url));
                            var appHrefs = Array.prototype.slice
                                .call(document.querySelectorAll('a[href]'))
                                .map(function(a) { return a.href; })
                                .filter(function(h) { return schemes.test(h); })
                                .slice(0, 10);
                            log('button click: "' + text.slice(0, 40) + '"' +
                                ' | selectedIntentUrl=' + window.selectedIntentUrl +
                                ' | resolved=' + url +
                                ' | intercepted=' + intercepted +
                                ' | appHrefs=' + JSON.stringify(appHrefs));
                            if (intercepted) {
                                e.stopImmediatePropagation();
                                e.preventDefault();
                                window.__pendingUpiUrl = url;
                            }
                        }, true);
                    }
                `,
            });
        };

        const onExit = () => {
            console.log('[UPI:exit] InAppBrowser exit event fired');
            clearInterval(retryPoller);
            history.replace(`${CommercialRootPath}/payment-status/${reqId}`);
        };

        const options = isAndroid
                ? 'hideurlbar=yes,hidenavigationbuttons=yes,beforeload=get'
                : 'location=no,hidenavigationbuttons=yes,beforeload=get';
        const ref = _window.cordova.InAppBrowser.open(url, '_blank', options);

        ref.addEventListener('beforeload', onBeforeLoad);
        ref.addEventListener('loadstart', onLoadStart);
        ref.addEventListener('loadstop', onLoadStop);
        ref.addEventListener('customscheme', onCustomScheme);
        ref.addEventListener('loaderror', onLoadError);
        ref.addEventListener('exit', onExit);

        // Polls the gateway page every 500ms to check for UPI dispatch requests.
        // This is the only reliable way to communicate from injected scripts back to
        // our Cordova context, since InAppBrowser doesn't support postMessage from
        // injected scripts. Dispatches intents via cordova.InAppBrowser.open(url, '_system')
        // which bypasses the WebView entirely - critical for subsequent dispatches after
        // the WebView's navigation engine is frozen.
        const retryPoller = setInterval(() => {
            ref.executeScript({ code: `
                (function() {
                    var logs = window.__upiLog || [];
                    window.__upiLog = [];
                    var url = '';
                    if (window.__pendingUpiUrl) {
                        url = window.__pendingUpiUrl;
                        window.__pendingUpiUrl = null;
                    } else if (window.__upiRetryRequested && window.__lastUpiUrl) {
                        window.__upiRetryRequested = false;
                        url = window.__lastUpiUrl;
                    }
                    return JSON.stringify({ url: url, logs: logs });
                })()
            ` }, (result: any) => {
                if (!result || !result[0]) return;
                const { url: upiUrl, logs = [] } = JSON.parse(result[0]);
                logs.forEach((line: string) => console.log('[UPI:page]', line));
                if (upiUrl) {
                    upiDispatched = false;
                    lastUpiUrl = upiUrl;
                    console.log('[UPI:poller] opening in system', toSystemUrl(upiUrl));
                    _window.cordova.InAppBrowser.open(toSystemUrl(upiUrl), '_system');
                    ref.executeScript({ code: `window.__lastUpiUrl = ${JSON.stringify(upiUrl)};` });
                }
            });
        }, 500);
    }

    const checkPaymentAvailable = async () => {
        //[#Fix Me With Types ---> ]
        const payload: any = {
            amc: location.state.amc,
            paymentType: paymentMode,
            isin: location.state.schemeOptions[0].isin,
            ifscCode:
                location.state.investorDetails.bank.ifsc ||
                location.state.investorDetails.bank.neftIfsc,
            vpa,
            userTrxnNo: userTrxnNo,
        };
        try {
            const response = (await dispatch(checkOnlinePayment(payload))) || {};
            const { body = {} }: any = response;
            const initErrors = checkPaymentTypesSupport(body);
            // const modes = body.amcPaymentTypesAllowed?.split(',') || [];
            // const availablePayments = modes.filter((key: string) => !initErrors[key]);
            // setPaymentTypesAllowed(availablePayments);
            // setPaymentMode(availablePayments[0]  === PaymentModeType.OTM  ? location.state.schemeOptions[0].trxnType === 'FP');
            setError(initErrors);
        } catch (err: any) {
            alert(err.message);
        }
    };

    console.log(location.state.amc);

    const payViaNetBanking = async () => {
        //[#Fix Me With Types ---> ]
        const payload: any = {
            amc: location.state.amc,
            paymentType: paymentMode,
            isin: location.state.schemeOptions[0].isin,
            ifscCode: location.state.investorDetails.bank.ifsc,
            vpa,
            userTrxnNo,
            reqId,
        };
        try {
            setLoading(LoadingStates.PAY);
            const response = (await dispatch(trxnPayOnline(payload))) || {};
            const { body = {} }: any = response;
            if (isMobileDevice()) {
                dispatch({
                    type: CLEAR_SCHEME_INFO,
                });
                onMobileRedirect(body.redirectUrl);
            } else {
                window.open(body.redirectUrl, '_self');
            }
        } catch (err: any) {
            alert(err.message);
        } finally {
            setLoading(null);
        }
    };

    const payViaOtm = async () => {
        try {
            const reqBody: OtmTrxnRequestType = {
                reqId,
                data: [
                    {
                        amc: location.state.amc,
                        paymentDetails: [
                            {
                                isin: location.state.schemeOptions[0].isin,
                                instNo: location.state.instNo,
                                instDateTime: '',
                                folio,
                                mandateRefId: selectedRows[0].payoutRefNo,
                                omUMRN: selectedRows[0].omUMRN,
                                payInMech: selectedRows[0].payoutMech,
                                userTrxnNo,
                                otmType: selectedRows[0].otmType,
                                bankCode: selectedRows[0].bank.code,
                                status: 'SUCCESS',
                                errorDescription: '',
                                paymentGatewayRequest: '',
                            },
                        ],
                    },
                ],
            };
            setLoading(LoadingStates.PAY);
            const response: any = await dispatch(paywithOTM(reqBody));
            const { body = {} } = response;
            dispatch({
                type: CLEAR_SCHEME_INFO,
            });
            history.replace(`${CommercialRootPath}/otm-payment-status/${body.reqId}`);
        } catch (err: any) {
            alert(err.message);
        } finally {
            setLoading(null);
        }
    };

    const checkPaymentTypesSupport = (data: any) => {
        const paymentTypesAvailable = data.amcPaymentTypesAllowed?.split(',') || [];
        const supportedPaymentTypes = [
            PaymentModeType.NB,
            PaymentModeType.OTM,
            PaymentModeType.UPI,
        ];
        const errors = supportedPaymentTypes.reduce((prev: any, curr: PaymentModeType) => {
            switch (curr) {
                case PaymentModeType.NB: {
                    if (!paymentTypesAvailable.includes(curr)) {
                        return {
                            ...prev,
                            [curr]: {
                                isError: true,
                                errorMessage: dbCommercial.newPurchase.validations.NB.notSupported,
                            },
                        };
                    }
                    if (data.bankNetBankingAllowedForAmc === IsAllow.No) {
                        return {
                            ...prev,
                            [curr]: {
                                isError: true,
                                errorMessage:
                                    dbCommercial.newPurchase.validations.NB.bankNotSupportError,
                            },
                        };
                    }
                    if (!data.bankPaymentTypesAllowed?.includes(PaymentModeType.NB)) {
                        return {
                            ...prev,
                            [curr]: {
                                isError: true,
                                errorMessage:
                                    dbCommercial.newPurchase.validations.NB.paymentProviderError,
                            },
                        };
                    }
                    return prev;
                }
                case PaymentModeType.UPI: {
                    if (!paymentTypesAvailable.includes(curr)) {
                        return {
                            ...prev,
                            [curr]: {
                                isError: true,
                                errorMessage: dbCommercial.newPurchase.validations.UPI.notSupported,
                            },
                        };
                    }
                    if (!data.bankPaymentTypesAllowed.includes(curr)) {
                        return {
                            ...prev,
                            [curr]: {
                                isError: true,
                                errorMessage:
                                    dbCommercial.newPurchase.validations.UPI.paymentProviderError,
                            },
                        };
                    }
                    return prev;
                }
                case PaymentModeType.OTM: {
                    if (!paymentTypesAvailable.includes(curr)) {
                        return {
                            ...prev,
                            [curr]: {
                                isError: true,
                                errorMessage: dbCommercial.newPurchase.validations.OTM.notSupported,
                            },
                        };
                    }
                    return prev;
                }
                default:
                    return prev;
            }
        }, {});
        return errors;
    };

    const handlePay = () => {
        switch (paymentMode) {
            case PaymentModeType.NB:
            case PaymentModeType.UPI: {
                payViaNetBanking();
                return;
            }
            case PaymentModeType.OTM: {
                payViaOtm();
                return;
            }
            default: {
            }
        }
    };

    if (!location.state) return null;
    return (
        <div className="curved-bg mf-investment mf-payment payment-screen">
            <ActionHeader header={`${getHeading(schemeOptions[0].trxnType)}`} />
            <div className="curved-bg-content">
                <div className="mf-investment-left">
                    <div className="investment-mode">
                        <h3>{dbCommercial.exploreMfs.mfPayment.selectPayment}</h3>
                        <div className="radio-div">
                            <CustomRadio
                                name="investing-through"
                                list={[
                                    {
                                        label: dbCommercial.exploreMfs.mfPayment.otm,
                                        value: PaymentModeType.OTM,
                                        radioImg: 'net-banking.png',
                                        disabled: error.OTM?.isError,
                                        isError: error.OTM?.isError,
                                        errorMessage: error.OTM?.errorMessage,
                                    },
                                    ...(['ASIP', 'FSIP'].includes(
                                        location.state.schemeOptions[0].trxnType,
                                    )
                                        ? []
                                        : [
                                            {
                                                label: dbCommercial.exploreMfs.mfPayment
                                                    .netBanking,
                                                value: PaymentModeType.NB,
                                                radioImg: 'netbanking_icon.svg',
                                                disabled: error.NB?.isError,
                                                isError: error.NB?.isError,
                                                errorMessage: error.NB?.errorMessage,
                                            },
                                            {
                                                label: dbCommercial.exploreMfs.mfPayment.upi,
                                                value: PaymentModeType.UPI,
                                                radioImg: 'upi.png',
                                                disabled: error.UPI?.isError,
                                                isError: error.UPI?.isError,
                                                errorMessage: error.UPI?.errorMessage,
                                            },
                                        ]),
                                ]}
                                extraClass="payment-mode"
                                selectedValue={paymentMode}
                                handleRadioChange={(value: PaymentModeType) =>
                                    setPaymentMode(value)
                                }
                            />
                        </div>
                        {paymentMode !== PaymentModeType.OTM && (
                            <div className="bank-name">
                                <p>{location.state.investorDetails.bank.name}</p>
                                <p>
                                    {maskAccountNo(location.state.investorDetails.bank.accountNo)}
                                </p>
                            </div>
                        )}
                        {/* {paymentMode === PaymentModeType.UPI && (
                            <div className="upi-field">
                                <p>{dbCommercial.exploreMfs.mfPayment.enterUPIId}</p>
                                <TextInput
                                    variant="outlined"
                                    name="vpa"
                                    value={vpa}
                                    focused={paymentMode === PaymentModeType.UPI}
                                    onChange={(e) => setVpa(e.target.value)}
                                />
                            </div>
                        )} */}
                    </div>
                    <div className="investment-details">
                        {paymentMode === PaymentModeType.OTM && (
                            <div className="curved-bg-content-table">
                                <>
                                    {!!loading || tableRows.length ? (
                                        <TableWithCollapse
                                            rows={tableRows}
                                            columns={tableColumns}
                                            selectedRows={selectedRows}
                                            setSelectedRows={setSelectedRows}
                                            renderCollapse={showCollapseData}
                                            radioGroupsEnabled
                                            toolTipEnabled
                                            toolTipTextGetter={(params) =>
                                                params.row.isDisabled
                                                    ? !params.row.omUMRN
                                                        ? dbCommercial.exploreMfs.mfPayment
                                                            .otmUmrnEmpty
                                                        : params.row.status?.toLowerCase() !==
                                                            'active'
                                                            ? dbCommercial.exploreMfs.mfPayment
                                                                .otmRowStatus
                                                            : sipType === 'SIP' &&
                                                                otmSipDateConditionNotValid(
                                                                    params.row.omStartDate,
                                                                    params.row.omExpiryDate,
                                                                    fromDate,
                                                                    toDate,
                                                                )
                                                                ? dbCommercial.exploreMfs.mfPayment
                                                                    .otmExpiried
                                                                : Number(params.row.maxAmount) <
                                                                    Number(amount)
                                                                    ? dbCommercial.exploreMfs.mfPayment
                                                                        .otmMaxAmountLabel
                                                                    : dbCommercial.exploreMfs.mfPayment
                                                                        .otmNotEligible
                                                    : ''
                                            }
                                            {...filters}
                                        />
                                    ) : (
                                        <NoFoliosFound
                                            info={`No OTM's found, Please register e-Mandate`}
                                            onClick={() => {
                                                history.push(`${CommercialRootPath}/otm-register`);
                                            }}
                                        />
                                    )}
                                </>
                            </div>
                        )}
                        <div className="mf-investment-bottom">
                            <div className="buttons">
                                <SubmitButtonWithLoader
                                    label={`${dbCommercial.exploreMfs.mfPayment.payVia}
                                    ${paymentMode === PaymentModeType.OTM
                                            ? PaymentModeType.OTM
                                            : paymentMode === PaymentModeType.NB
                                                ? 'Net Banking'
                                                : paymentMode === PaymentModeType.UPI
                                                    ? 'UPI'
                                                    : ''
                                        }`}
                                    loading={loading === LoadingStates.PAY}
                                    labelWhileLoading={
                                        dbCommercial.newPurchase.paymentFlow.initiatingPayment
                                    }
                                    className="customBtn "
                                    onClick={handlePay}
                                    disabled={
                                        !paymentMode ||
                                        (paymentMode === PaymentModeType.OTM &&
                                            (data.length === 0 || selectedRows.length === 0))
                                    }
                                ></SubmitButtonWithLoader>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mf-investment-right">
                    <div className="heading">
                        <h4>
                            {dbCommercial.exploreMfs.mfPayment.investmentDetails}{' '}
                            <img src="/images/rupee-coin.png" alt="icon" />
                        </h4>
                        <p>
                            <img src={location.state.logoUrl} alt="icon" />
                            {location.state.schemeName}
                        </p>
                    </div>
                    <div className="profile-details">
                        <div className="profile-details-info">
                            <p>{dbCommercial.exploreMfs.mfPayment.investmentType}</p>
                            <h6>{displayTransactionType(trxnType)}</h6>
                        </div>
                        <div className="profile-details-info">
                            <p>{dbCommercial.exploreMfs.mfPayment.investmentAmount}</p>
                            <h6>
                                <b>{formatAsIndianCurrency(Number(amount))}</b>
                            </h6>
                        </div>
                        <div className="profile-details-info">
                            <p>{dbCommercial.exploreMfs.mfPayment.transactionNumber}</p>
                            <h6>{userTrxnNo}</h6>
                        </div>
                    </div>
                </div>
            </div>
            <Dialog
                maxWidth={'sm'}
                open={show}
                aria-labelledby="newbank-mandate-modal"
                onClose={() => setShow(false)}
            >
                <div className="note-modals newbank-mandate-modal">
                    <h3>{dbCommercial.exploreMfs.mfPayment.modal.heading}</h3>
                    <a onClick={() => setShow(!show)} className="close">
                        <img src="/images/close-black.svg" alt="icon" />
                    </a>
                    <div className="note-modals-content">
                        <div className="mandate-amount">
                            <TextField
                                label={dbCommercial.exploreMfs.mfPayment.modal.mandateAmount}
                                id="enter-arn"
                                variant="outlined"
                            />
                            <p>
                                <img src="/images/exclaim.svg" alt="icon" />
                                Valid From (Today)
                                <span>30-12-2021</span>
                            </p>
                        </div>
                        <FormControl variant="filled" className="bank-account-select">
                            <Select labelId="reg-mode-label" id="reg-mode-sortby" value="0">
                                <MenuItem value={0}>
                                    {dbCommercial.exploreMfs.mfPayment.modal.bankAccount}
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label={dbCommercial.exploreMfs.mfPayment.modal.ifsc}
                            id="enter-arn"
                            variant="outlined"
                        />
                        <div className="note-modals-btns">
                            <Button className="customBtn" onClick={() => setShow(!show)}>
                                {dbCommercial.exploreMfs.mfPayment.modal.submit}
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

const Navigator = (props: any) => {
    const history = useHistory();

    useEffect(() => {
        const handler = (event: any) => {
            console.log('Page show !!!', event);
            if (event.persisted === true) {
                console.log('Persisted!!! ');

                console.log('History  == ', history);
                // if (history.action == 'POP') {
                window.history.back();
                // }
            }
        };
        if (history.action == 'POP') {
            window.history.back();
        }

        window.addEventListener('pageshow', handler);

        return () => {
            console.log('Unmounting...');
            window.removeEventListener('pageshow', handler);
        };
    }, []);

    return <MfPayment {...props} {...history} />;
};
export default Navigator;
